import createCache from '@emotion/cache';
import { CacheProvider } from '@emotion/react';
import * as FullStory from '@fullstory/browser';
import React, { lazy, Suspense } from 'react';
import {
  theme as biomeTheme,
  ThemeProvider as BiomeThemeProvider
  // THEME_ID
} from '@biome-design-system/styles';
import { loadableReady } from '@loadable/component';
import ReactDOM from 'react-dom';
import { DynamicIntlProvider } from './intl/DynamicIntlProvider';
import { Provider as ReduxProvider } from 'react-redux';
import { BrowserRouter } from 'react-router-dom';
import kyruusTheme from '@kyruus/ui-theme';
import { isCancelFlow, isManageAppointmentFlow } from 'Common/urls/util';
import {
  getConfigProperty,
  shouldRenderDirectBookInDrawer
} from '../../common/config/utils';
import { setTestConfigs, setConfig } from './behaviors/configuration';
import { initializeLogging } from './tracking';
import configureStore from './store/configure-store';
import { getInitialStateFromPage } from './utils/state';
import { initDatadogRUM } from './utils/initDatadogRUM';

import { ThemeProvider } from '@emotion/react';
import { productNameSelector } from './behaviors/product-name';
import { clearAllFetchedData } from './behaviors/actions';
import { APP_ID, EDITOR_APP_ID } from './utils/constants';
import { isbot } from 'isbot';
import { ClientApp } from './client-app';

// DEATH TO JQUERY!
function ready(fn) {
  if (window.document.readyState != 'loading') {
    fn();
  } else {
    window.document.addEventListener('DOMContentLoaded', fn);
  }
}

function isSessionFromBot() {
  const userAgent = window.navigator.userAgent;
  if (!userAgent) {
    return false;
  }
  if (userAgent.includes('HeadlessChrome/')) {
    return false;
  }
  return isbot(userAgent);
}

const PmcEditor = lazy(() => import('./editor/editor'));

ready(() => {
  const initialState = getInitialStateFromPage();
  if (!isSessionFromBot()) {
    initDatadogRUM({ initialState });
  }
  const store = configureStore(initialState);

  const renderApp = () => {
    const currentState = store.getState();
    const {
      config,
      customerCode,
      authenticatedUserInfo,
      settings,
      loggingMetadata
    } = currentState;

    const productName = productNameSelector(currentState);

    const {
      locale = 'en-US',
      application_string_templates = {},
      theme = {}
    } = config;

    let themeToRenderDrawerInDirectBook = {};

    const nonceMetaElement = window.document.querySelector(
      `meta[property='csp-nonce']`
    );

    const styleNonce = nonceMetaElement?.content;

    const { log } = initializeLogging({
      loggingMetadata,
      customerConfig: config,
      styleNonce
    });

    // If the feature flag is enabled and the current flow is not a cancel or manage appointment flow,
    // set the drawer screen sizes to a large value to prevent the drawer from rendering desktop content.
    if (
      shouldRenderDirectBookInDrawer(config) &&
      !(isCancelFlow() || isManageAppointmentFlow())
    ) {
      themeToRenderDrawerInDirectBook = {
        db_screen_large: '10000px',
        db_screen_medium: '10000px',
        db_screen_small: '10000px',
        db_screen_xsmall: '10000px'
      };
    }

    // NOTE: THIS `console.log` call IS used for `functional tests`
    // hammerhead exists when testcafe runs
    // https://stackoverflow.com/a/55451665
    if (window && window['%hammerhead%']) {
      window.__setTestConfig = (override) => {
        store.dispatch(setTestConfigs(override));
      };
    }

    window.addEventListener(
      'message',
      (event) => {
        // only accept events from the same origin, and window, not from other domains / iframe hosts, etc
        // Eventually if we hook this up from a different kyruus domain or PMA or somesuch, we'll want to
        // adjust this logic further
        if (
          event.origin === window.location.origin &&
          event.source === window
        ) {
          if (
            event.data?.eventName === 'pmc_editor:set_config' &&
            event.data?.eventData
          ) {
            // clear any fetched data so that it gets re-fetched with the new config
            store.dispatch(clearAllFetchedData());
            store.dispatch(setConfig(event.data.eventData));
            window.debug_rerender();
          }
        }
      },
      false
    );

    // Hydration of the ids in `data-emotion-css` will automatically occur when the cache is created
    const emotionCache = createCache({
      key: 'css',
      nonce: styleNonce,
      prepend: true
    });

    const customerBiomeTheme = {
      ...biomeTheme,
      ...theme,
      ...themeToRenderDrawerInDirectBook
    };
    const customerTheme = {
      ...kyruusTheme,
      ...theme,
      ...themeToRenderDrawerInDirectBook
    };

    ReactDOM.render(
      <ReduxProvider store={store}>
        <CacheProvider value={emotionCache}>
          <ThemeProvider theme={customerTheme}>
            <BiomeThemeProvider theme={customerBiomeTheme}>
              <DynamicIntlProvider
                locale={locale}
                defaultLocale="en-US"
                messages={application_string_templates}
              >
                <BrowserRouter>
                  <ClientApp
                    dispatch={store.dispatch}
                    log={log}
                    config={config}
                    customerCode={customerCode}
                    productName={productName}
                    authenticatedUserInfo={authenticatedUserInfo}
                  />
                </BrowserRouter>
              </DynamicIntlProvider>
            </BiomeThemeProvider>
          </ThemeProvider>
        </CacheProvider>
      </ReduxProvider>,
      window.document.getElementById(APP_ID)
    );

    const fullstoryEnabled = getConfigProperty(
      config,
      'third_party.fullstory.enabled'
    );

    // init full story when enabled
    if (fullstoryEnabled === true && settings.FULLSTORY_ORG_ID != null) {
      FullStory.init({
        orgId: settings.FULLSTORY_ORG_ID,
        // add support for iframed environments
        recordOnlyThisIFrame: true
      });
    }
  };

  const renderEditorApp = () => {
    const isEditorEnabled = window.admin?.isEditorActive();

    if (!isEditorEnabled) {
      // if editor cookie is disabled, don't render it
      return;
    }
    let domElement = window.document.getElementById(EDITOR_APP_ID);
    if (domElement) {
      // if element already exists, exit here as we've already rendered it
      return;
    }

    const { config } = store.getState();

    const { locale = 'en-US', application_string_templates = {} } = config;

    // create the dom element for us to render the react tree into
    domElement = window.document.createElement('div');
    domElement.id = EDITOR_APP_ID;
    window.document.body.appendChild(domElement);

    const nonceMetaElement = window.document.querySelector(
      `meta[property='csp-nonce']`
    );

    const styleNonce = nonceMetaElement?.content;

    ReactDOM.render(
      <CacheProvider
        value={createCache({
          key: 'css',
          nonce: styleNonce,
          prepend: true
        })}
      >
        <ThemeProvider theme={kyruusTheme}>
          <BiomeThemeProvider theme={biomeTheme}>
            <DynamicIntlProvider
              locale={locale}
              defaultLocale="en-US"
              messages={application_string_templates}
            >
              <Suspense fallback={<div>Loading...</div>}>
                <PmcEditor />
              </Suspense>
            </DynamicIntlProvider>
          </BiomeThemeProvider>
        </ThemeProvider>
      </CacheProvider>,
      domElement
    );
  };

  loadableReady(() => {
    renderApp();
    renderEditorApp();
    // send event that we can hook into for testing
    window.pmc_ready = true;
    const readyEvt = new Event('pmc_ready');
    window.dispatchEvent(readyEvt);
  });

  // re-render the entire app client-side. for debug use only
  window.debug_rerender = () => {
    ReactDOM.unmountComponentAtNode(window.document.getElementById(APP_ID));
    // use existing store if a new one didnt get passed in
    renderApp();
  };

  // simple utils for speeding up dev by allowing bookmarklets to
  // access these features more easily and future-proof-ly
  window.admin = {
    enableEditor: () => {
      window.document.cookie =
        'debug_editor=true;path=/;secure;samesite=None;partitioned';
      renderEditorApp();
    },
    disableEditor: () => {
      window.document.cookie =
        'debug_editor=false;path=/;expires=Thu, 01 Jan 1970 00:00:01 GMT;secure;samesite=None;partitioned';
      window.admin.disableStringDebug();
      const domElem = window.document.getElementById(EDITOR_APP_ID);
      if (!domElem) {
        return;
      }
      ReactDOM.unmountComponentAtNode(domElem);
      domElem.remove();
    },
    // check if the editor is current enabled, for use in callbacks and the such, not for rendering
    isEditorActive: () => {
      return window.document.cookie.indexOf('debug_editor=true') > -1;
    },
    enableStringDebug: () => {
      window.document.cookie = 'string_debug=true;path=/';
      window.debug_rerender();
    },
    disableStringDebug: () => {
      window.document.cookie =
        'string_debug=false;path=/;expires=Thu, 01 Jan 1970 00:00:01 GMT';
      window.debug_rerender();
    }
  };
});
